import { z } from "zod";

export const accessionFormFieldKeys = [
  "subjectId",
  "caseType",
  "orgId",
  "billToOrgId",
  "splitBillingOrgs",
  "sharedOrgs",
  "additionalEmails",
  "receivingOrgId",
  "instructions",
  "samples",
  "packedAt",
  "trackingId",
  "requisitionId",
  "priority",
] as const;

export const definitionOverrideScopeSchema = z.enum([
  "global",
  "org",
  "subjectType",
  "org+subjectType",
]);

export const accessionFormFieldKeySchema = z.enum(accessionFormFieldKeys);

export const accessionFormFieldDefinitionSchema = z.object({
  key: accessionFormFieldKeySchema,
  label: z.string().optional(),
  description: z.string().optional(),
  required: z.boolean().prefault(false),
  locked: z.boolean().prefault(false),
  order: z.int().min(0),
});

export const accessionFormSectionDefinitionSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  order: z.int().min(0),
  fields: z.array(accessionFormFieldDefinitionSchema),
});

export const accessionFormDefinitionSchemaV1 = z.object({
  version: z.literal(1),
  sections: z.array(accessionFormSectionDefinitionSchema),
});

export const accessionFormOverrideDefinitionSchema = z.object({
  scope: definitionOverrideScopeSchema,
  orgId: z.int().positive().optional(),
  subjectType: z.string().min(1).optional(),
  definition: accessionFormDefinitionSchemaV1,
});

export const accessionFormDefinitionEnvelopeSchemaV1 = z.object({
  version: z.literal(1),
  defaultDefinition: accessionFormDefinitionSchemaV1,
  overrides: z.array(accessionFormOverrideDefinitionSchema).prefault([]),
});

export type AccessionFormFieldKey = z.infer<typeof accessionFormFieldKeySchema>;
export type DefinitionOverrideScope = z.infer<
  typeof definitionOverrideScopeSchema
>;
export type AccessionFormFieldDefinition = z.infer<
  typeof accessionFormFieldDefinitionSchema
>;
export type AccessionFormSectionDefinition = z.infer<
  typeof accessionFormSectionDefinitionSchema
>;
export type AccessionFormDefinitionV1 = z.infer<
  typeof accessionFormDefinitionSchemaV1
>;
export type AccessionFormOverrideDefinition = z.infer<
  typeof accessionFormOverrideDefinitionSchema
>;
export type AccessionFormDefinitionEnvelopeV1 = z.infer<
  typeof accessionFormDefinitionEnvelopeSchemaV1
>;

export function resolveAccessionFormDefinition(
  envelope: AccessionFormDefinitionEnvelopeV1,
  context?: { orgId?: number | null; subjectType?: string | null },
) {
  const orgId = context?.orgId ?? null;
  const subjectType = context?.subjectType ?? null;

  const bySpecificity = [
    (override: AccessionFormOverrideDefinition) =>
      override.scope === "org+subjectType" &&
      override.orgId === orgId &&
      override.subjectType === subjectType,
    (override: AccessionFormOverrideDefinition) =>
      override.scope === "org" && override.orgId === orgId,
    (override: AccessionFormOverrideDefinition) =>
      override.scope === "subjectType" && override.subjectType === subjectType,
    (override: AccessionFormOverrideDefinition) => override.scope === "global",
  ];

  for (const matcher of bySpecificity) {
    const match = envelope.overrides.find(matcher);
    if (match) {
      return match.definition;
    }
  }

  return envelope.defaultDefinition;
}
